/**
 * Helpers for extracting real email metadata from forwarded emails.
 * All emails arrive as forwards — the real sender/subject/date are in extraction.metadata.
 * Fallbacks handle the case where the BE hasn't populated these fields yet.
 */
import type { ProcessingRequest } from "../api/types";

const FORWARD_PREFIX_RE = /^(?:I:\s*|FW:\s*|Fwd:\s*|Re:\s*)+/i;

/** Strip forward prefixes (I:, FW:, Fwd:, Re:) from a subject line. */
export function stripForwardPrefixes(subject: string | undefined | null): string {
  if (!subject) return "";
  return subject.replace(FORWARD_PREFIX_RE, "").trim();
}

/** Get extraction.metadata safely from a ProcessingRequest. */
function getExtractionMetadata(r: ProcessingRequest): Record<string, unknown> {
  const ext = r.extraction as Record<string, unknown> | null;
  if (!ext) return {};
  const meta = ext.metadata as Record<string, unknown> | null;
  return meta ?? {};
}

/** Get extraction object safely. */
function getExtraction(r: ProcessingRequest): Record<string, unknown> {
  return (r.extraction as Record<string, unknown> | null) ?? {};
}

/**
 * Parse "From: Name <email>" from the first line of an extract_eml attachment text.
 * Returns the sender string or null.
 */
function parseSenderFromEmlText(r: ProcessingRequest): string | null {
  const ext = getExtraction(r);
  const atts = ext.attachments_text as Array<Record<string, unknown>> | null;
  if (!atts?.length) return null;
  const first = atts[0];
  if (first.method !== "extract_eml") return null;
  const text = first.extracted_text as string | null;
  if (!text) return null;
  const m = text.match(/^From:\s*(.+)/i);
  return m ? m[1].trim() : null;
}

/**
 * Parse "Da: Name" from body_clean (Outlook-forwarded emails).
 * The Italian format is: "Da: Name\nInviato: ..."
 */
function parseSenderFromBodyClean(r: ProcessingRequest): string | null {
  const ext = getExtraction(r);
  const bc = ext.body_clean as string | null;
  if (!bc) return null;
  const m = bc.match(/^Da:\s*(.+?)(?:\r?\n|Per conto di)/i);
  if (!m) return null;
  const raw = m[1].trim();
  // Clean trailing "Per conto di..." or similar noise
  return raw.replace(/\s*Per conto di.*$/i, "").trim() || null;
}

/** Filter "trusty" alias and extract name from "Name <email>" format. */
function cleanSender(raw: string | null, nameOnly: boolean): string | null {
  if (!raw) return null;
  if (/trusty/i.test(raw) && /segnalazion/i.test(raw)) return null;
  if (nameOnly) {
    const m = raw.match(/^(.+?)\s*<[^>]+>$/);
    return m ? m[1].trim() : raw;
  }
  return raw;
}

/**
 * Get the original sender (name only, not email).
 * Priority: BE field → extraction.metadata → eml text → body_clean → incoming_data
 */
export function getOriginalSender(r: ProcessingRequest): string | null {
  const meta = r.original_sender
    || getExtractionMetadata(r).original_sender as string | null
    || null;
  if (meta) return cleanSender(meta, true);

  const fromEml = parseSenderFromEmlText(r);
  if (fromEml) return cleanSender(fromEml, true);

  const fromBc = parseSenderFromBodyClean(r);
  if (fromBc) return cleanSender(fromBc, true);

  const incoming = r.incoming_data?.sender?.name
    || r.incoming_data?.sender?.email
    || r.incoming_data?.sender?.phone
    || null;
  return cleanSender(incoming, true);
}

/**
 * Get the full original sender string (name + email) for detail view.
 */
export function getOriginalSenderFull(r: ProcessingRequest): string | null {
  const meta = r.original_sender
    || getExtractionMetadata(r).original_sender as string | null
    || null;
  if (meta) return cleanSender(meta, false);

  const fromEml = parseSenderFromEmlText(r);
  if (fromEml) return cleanSender(fromEml, false);

  const fromBc = parseSenderFromBodyClean(r);
  if (fromBc) return cleanSender(fromBc, false);

  const s = r.incoming_data?.sender;
  // Il TELEFONO chiude la catena. Su una pratica nata da conversazione e'
  // l'unico campo che il materializer valorizza (`SenderInfo(name=referente,
  // phone=session.phone_e164)`, e il referente spesso non c'e'): senza questo
  // ramo il dettaglio scriveva "DA: —" pur avendo il numero del chiamante in
  // `incoming_data`, e l'operatore non sapeva a chi stava per rispondere.
  const incoming =
    s?.name && s?.email ? `${s.name} <${s.email}>`
    : s?.email ? s.email
    : s?.name && s?.phone ? `${s.name} (${s.phone})`
    : s?.name || s?.phone || null;
  return cleanSender(incoming, false);
}

/**
 * Get the clean subject (without forward prefixes).
 * Priority: top-level BE field → extraction.metadata → incoming_data.subject
 */
export function getCleanSubject(r: ProcessingRequest): string {
  const origSubject = r.original_subject
    || getExtractionMetadata(r).original_subject as string | null
    || null;
  if (origSubject) return stripForwardPrefixes(origSubject);
  return stripForwardPrefixes(r.incoming_data?.subject);
}

/**
 * Get the original date (when the email was actually sent).
 * Priority: top-level BE field → extraction.metadata → incoming_data.received_at → created_at
 */
export function getOriginalDate(r: ProcessingRequest): string | null {
  const origDate = r.original_date
    || getExtractionMetadata(r).original_date as string | null
    || null;
  if (origDate) return origDate;
  return r.incoming_data?.received_at || r.created_at || null;
}

/** Get the received_at date (when the system received the forwarded email). */
export function getReceivedDate(r: ProcessingRequest): string | null {
  return r.incoming_data?.received_at || r.created_at || null;
}
